/**
 * Catálogo de módulos de la plataforma: la única definición del menú.
 *
 * Cada entrada declara el permiso que la habilita (`<area>.<ruta>.access`).
 * Los mismos códigos se siembran en el backend, así que agregar una opción aquí
 * y volver a correr el seeder de roles es todo lo necesario para exponerla.
 *
 * Un módulo con submenú no tiene permiso propio: se muestra si el usuario puede
 * ver al menos una de sus opciones.
 */

export interface NavSubItem {
  titulo: string;
  routerLink: string;
  /** Código de permiso que habilita esta opción. */
  permiso: string;
  /** Rutas de detalle o de alta que pertenecen a esta opción (mantienen el menú activo). */
  relacionadas?: string[];
  href?: string;
}

export interface NavItem {
  titulo: string;
  icono: string;
  slug?: string;
  routerLink?: string;
  permiso?: string;
  submenu?: NavSubItem[];
  isHeader?: boolean;
}

/** Área de la aplicación a la que pertenece cada rol. */
export type AreaApp = 'admin' | 'docente' | 'estudiante';


export const MENU_ADMIN: NavItem[] = [
    {
      titulo: "Dashboard",
      icono: "bi-house-fill",
      slug: "dashboard",
      routerLink: "/admin/dashboard",
      permiso: "admin.dashboard.access",
    },
    {
      titulo: "Gestion Academica",
      icono: "bi-mortarboard-fill",
      submenu: [
        {
          titulo: "Programas Academicos",
          routerLink: "/admin/gestion-academica/programas-academicos",
          permiso: "admin.gestion-academica.programas-academicos.access"
        },
        {
          titulo: "Gestión de PEA",
          routerLink: "/admin/gestion-academica/pea",
          permiso: "admin.gestion-academica.pea.access"
        },
        {
          titulo: "Planes Estudio",
          routerLink: "/admin/gestion-academica/planes-estudio",
          permiso: "admin.gestion-academica.planes-estudio.access"
        },
        {
          titulo: "Microcurriculos",
          routerLink: "/admin/gestion-academica/microcurriculos",
          permiso: "admin.gestion-academica.microcurriculos.access"
        },
        {
          titulo: "Resultados Aprendizaje",
          routerLink: "/admin/gestion-academica/resultados-aprendizaje",
          permiso: "admin.gestion-academica.resultados-aprendizaje.access"
        },
        {
          titulo: "Asignaturas",
          routerLink: "/admin/gestion-academica/asignaturas",
          permiso: "admin.gestion-academica.asignaturas.access"
        },
        {
          titulo: "Programación Académica",
          routerLink: "/admin/gestion-academica/horarios-academicos",
          permiso: "admin.gestion-academica.horarios-academicos.access"
        },
        {
          titulo: "Control Grupos",
          routerLink: "/admin/gestion-academica/control-grupos",
          permiso: "admin.gestion-academica.control-grupos.access"
        },
        {
          titulo: "Homologaciones",
          routerLink: "/admin/gestion-academica/homologaciones",
          permiso: "admin.gestion-academica.homologaciones.access"
        },
        {
          titulo: "Reintegras",
          routerLink: "/admin/gestion-academica/reintegras",
          permiso: "admin.gestion-academica.reintegras.access"
        },
        {
          titulo: "Calendario institucional",
          routerLink: "/admin/gestion-academica/calendario-institucional",
          permiso: "admin.gestion-academica.calendario-institucional.access"
        },
        {
          titulo: "Espacios y recursos",
          routerLink: "/admin/gestion-academica/espacios-recursos",
          permiso: "admin.gestion-academica.espacios-recursos.access"
        },
        {
          titulo: "Proyectos de grado",
          routerLink: "/admin/gestion-academica/proyectos-grado",
          permiso: "admin.gestion-academica.proyectos-grado.access"
        },
        {
          titulo: "Reglas de puntos",
          routerLink: "/admin/gestion-academica/reglas-puntos",
          permiso: "admin.gestion-academica.reglas-puntos.access"
        },
      ],
    },
    {
      titulo: "Gestion Docente",
      icono: "bi-person-badge-fill",
      submenu: [
        {
          titulo: "Gestión Docentes",
          routerLink: "/admin/gestion-docente/perfil-docente",
          permiso: "admin.gestion-docente.perfil-docente.access",
          relacionadas: ["/admin/gestion-docente/vinculacion-docente"]
        },
        {
          titulo: "Asignacion Academica",
          routerLink: "/admin/gestion-docente/asignacion-academica",
          permiso: "admin.gestion-docente.asignacion-academica.access"
        },
        {
          titulo: "Evaluacion Docente",
          routerLink: "/admin/gestion-docente/evaluacion-docente",
          permiso: "admin.gestion-docente.evaluacion-docente.access"
        },
        {
          titulo: "Seguimiento Desempenio",
          routerLink: "/admin/gestion-docente/seguimiento-desempenio",
          permiso: "admin.gestion-docente.seguimiento-desempenio.access"
        },
      ],
    },
    {
      titulo: "Gestion Estudiantil",
      icono: "bi-people-fill",
      submenu: [
        {
          titulo: "Gestión Estudiantes",
          routerLink: "/admin/gestion-estudiantil/estudiantes",
          permiso: "admin.gestion-estudiantil.estudiantes.access",
          relacionadas: ["/admin/gestion-estudiantil/perfil-estudiante"]
        },
        {
          titulo: "Matriculas",
          routerLink: "/admin/gestion-estudiantil/matriculas",
          permiso: "admin.gestion-estudiantil.matriculas.access"
        },
        {
          titulo: "Historial Academico",
          routerLink: "/admin/gestion-estudiantil/historial-academico",
          permiso: "admin.gestion-estudiantil.historial-academico.access"
        },
        {
          titulo: "Seguimiento Academico",
          routerLink: "/admin/gestion-estudiantil/seguimiento-academico",
          permiso: "admin.gestion-estudiantil.seguimiento-academico.access"
        },
        {
          titulo: "Control Permanencia",
          routerLink: "/admin/gestion-estudiantil/control-permanencia",
          permiso: "admin.gestion-estudiantil.control-permanencia.access"
        },
        {
          titulo: "Alertas Desercion",
          routerLink: "/admin/gestion-estudiantil/alertas-desercion",
          permiso: "admin.gestion-estudiantil.alertas-desercion.access"
        },
        {
          titulo: "Certificados",
          routerLink: "/admin/gestion-estudiantil/certificados",
          permiso: "admin.gestion-estudiantil.certificados.access"
        },
      ],
    },
    {
      titulo: "Quédate",
      icono: "bi-heart-fill",
      submenu: [
        {
          titulo: "Puntos (XP)",
          routerLink: "/admin/stay/xp-points",
          permiso: "admin.stay.xp-points.access"
        },
      ],
    },
    {
      titulo: "Calidad",
      icono: "bi-award-fill",
      submenu: [
        {
          titulo: "Autoevaluacion Institucional",
          routerLink: "/admin/calidad/autoevaluacion-institucional",
          permiso: "admin.calidad.autoevaluacion-institucional.access"
        },
        {
          titulo: "Planes Mejoramiento",
          routerLink: "/admin/calidad/planes-mejoramiento",
          permiso: "admin.calidad.planes-mejoramiento.access"
        },
        {
          titulo: "Indicadores Calidad",
          routerLink: "/admin/calidad/indicadores-calidad",
          permiso: "admin.calidad.indicadores-calidad.access"
        },
        {
          titulo: "Evidencias Documentales",
          routerLink: "/admin/calidad/evidencias-documentales",
          permiso: "admin.calidad.evidencias-documentales.access"
        },
        {
          titulo: "Seguimiento Resultados Aprendizaje",
          routerLink: "/admin/calidad/seguimiento-resultados-aprendizaje",
          permiso: "admin.calidad.seguimiento-resultados-aprendizaje.access"
        },
        {
          titulo: "Evaluaciones Institucionales",
          routerLink: "/admin/calidad/evaluaciones-institucionales",
          permiso: "admin.calidad.evaluaciones-institucionales.access"
        },
        {
          titulo: "Informes MEN",
          routerLink: "/admin/calidad/informes-men",
          permiso: "admin.calidad.informes-men.access"
        },
      ],
    },
    {
      titulo: "Investigacion",
      icono: "bi-search",
      submenu: [
        {
          titulo: "Grupos Investigacion",
          routerLink: "/admin/investigacion/grupos-investigacion",
          permiso: "admin.investigacion.grupos-investigacion.access"
        },
        {
          titulo: "Proyectos Activos",
          routerLink: "/admin/investigacion/proyectos-activos",
          permiso: "admin.investigacion.proyectos-activos.access"
        },
        {
          titulo: "Produccion Academica",
          routerLink: "/admin/investigacion/produccion-academica",
          permiso: "admin.investigacion.produccion-academica.access"
        },
        {
          titulo: "Semilleros",
          routerLink: "/admin/investigacion/semilleros",
          permiso: "admin.investigacion.semilleros.access"
        },
        {
          titulo: "Indicadores Investigacion",
          routerLink: "/admin/investigacion/indicadores-investigacion",
          permiso: "admin.investigacion.indicadores-investigacion.access"
        },
      ],
    },
    {
      titulo: "Extension Proyeccion",
      icono: "bi-globe",
      submenu: [
        {
          titulo: "Educacion Continua",
          routerLink: "/admin/extension-proyeccion/educacion-continua",
          permiso: "admin.extension-proyeccion.educacion-continua.access"
        },
        {
          titulo: "Convenios",
          routerLink: "/admin/extension-proyeccion/convenios",
          permiso: "admin.extension-proyeccion.convenios.access"
        },
        {
          titulo: "Practicas Empresariales",
          routerLink: "/admin/extension-proyeccion/practicas-empresariales",
          permiso: "admin.extension-proyeccion.practicas-empresariales.access"
        },
        {
          titulo: "Relacionamiento Externo",
          routerLink: "/admin/extension-proyeccion/relacionamiento-externo",
          permiso: "admin.extension-proyeccion.relacionamiento-externo.access"
        },
        {
          titulo: "Impacto Social",
          routerLink: "/admin/extension-proyeccion/impacto-social",
          permiso: "admin.extension-proyeccion.impacto-social.access"
        },
      ],
    },
    {
      titulo: "Gestion Financiera",
      icono: "bi-cash-coin",
      submenu: [
        {
          titulo: "Estado Financiero Institucional",
          routerLink: "/admin/gestion-financiera/estado-financiero-institucional",
          permiso: "admin.gestion-financiera.estado-financiero-institucional.access"
        },
        {
          titulo: "Pagos Cartera",
          routerLink: "/admin/gestion-financiera/pagos-cartera",
          permiso: "admin.gestion-financiera.pagos-cartera.access"
        },
        {
          titulo: "Acuerdos Pago",
          routerLink: "/admin/gestion-financiera/acuerdos-pago",
          permiso: "admin.gestion-financiera.acuerdos-pago.access"
        },
        {
          titulo: "Reportes Financieros",
          routerLink: "/admin/gestion-financiera/reportes-financieros",
          permiso: "admin.gestion-financiera.reportes-financieros.access"
        },
        {
          titulo: "Facturacion",
          routerLink: "/admin/gestion-financiera/facturacion",
          permiso: "admin.gestion-financiera.facturacion.access"
        },
      ],
    },
    {
      titulo: "Bienestar Institucional",
      icono: "bi-heart-fill",
      submenu: [
        {
          titulo: "Reporte de participación",
          routerLink: "/admin/bienestar-institucional/participation-report",
          permiso: "admin.bienestar-institucional.participation-report.access"
        },
        {
          titulo: "Programas Bienestar",
          routerLink: "/admin/bienestar-institucional/programas-bienestar",
          permiso: "admin.bienestar-institucional.programas-bienestar.access"
        },
        {
          titulo: "Seguimiento Psicosocial",
          routerLink: "/admin/bienestar-institucional/seguimiento-psicosocial",
          permiso: "admin.bienestar-institucional.seguimiento-psicosocial.access"
        },
        {
          titulo: "Actividades Culturales",
          routerLink: "/admin/bienestar-institucional/actividades-culturales",
          permiso: "admin.bienestar-institucional.actividades-culturales.access"
        },
        {
          titulo: "Encuestas Institucionales",
          routerLink: "/admin/bienestar-institucional/encuestas-institucionales",
          permiso: "admin.bienestar-institucional.encuestas-institucionales.access"
        },
      ],
    },
    {
      titulo: "Documentacion Institucional",
      icono: "bi-folder-fill",
      submenu: [
        {
          titulo: "PEI",
          routerLink: "/admin/documentacion-institucional/pei",
          permiso: "admin.documentacion-institucional.pei.access"
        },
        {
          titulo: "Reglamento Estudiantil",
          routerLink: "/admin/documentacion-institucional/reglamento-estudiantil",
          permiso: "admin.documentacion-institucional.reglamento-estudiantil.access"
        },
        {
          titulo: "Reglamento Docente",
          routerLink: "/admin/documentacion-institucional/reglamento-docente",
          permiso: "admin.documentacion-institucional.reglamento-docente.access"
        },
        {
          titulo: "Estatutos",
          routerLink: "/admin/documentacion-institucional/estatutos",
          permiso: "admin.documentacion-institucional.estatutos.access"
        },
        {
          titulo: "Politicas Institucionales",
          routerLink: "/admin/documentacion-institucional/politicas-institucionales",
          permiso: "admin.documentacion-institucional.politicas-institucionales.access"
        },
        {
          titulo: "Manuales Internos",
          routerLink: "/admin/documentacion-institucional/manuales-internos",
          permiso: "admin.documentacion-institucional.manuales-internos.access"
        },
        {
          titulo: "Gestional Documental",
          routerLink: "/admin/documentacion-institucional/gestional-documental",
          permiso: "admin.documentacion-institucional.gestional-documental.access"
        },
      ],
    },
    {
      titulo: "Gestion Usuarios",
      icono: "bi-person-fill-gear",
      submenu: [
        {
          titulo: "Administrativos",
          routerLink: "/admin/gestion-usuarios/listado-usuarios",
          permiso: "admin.gestion-usuarios.listado-usuarios.access",
          relacionadas: ["/admin/gestion-usuarios/usuarios", "/admin/gestion-usuarios/perfil-usuario"]
        },
        {
          titulo: "Asignar Roles",
          routerLink: "/admin/gestion-usuarios/asignar-roles",
          permiso: "admin.gestion-usuarios.asignar-roles.access"
        },
        {
          titulo: "Control Accesos",
          routerLink: "/admin/gestion-usuarios/control-accesos",
          permiso: "admin.gestion-usuarios.control-accesos.access"
        },
        {
          titulo: "Auditorias Sesiones",
          routerLink: "/admin/gestion-usuarios/auditorias-sesiones",
          permiso: "admin.gestion-usuarios.auditorias-sesiones.access"
        },
        {
          titulo: "Permisos",
          routerLink: "/admin/gestion-usuarios/permisos",
          permiso: "admin.gestion-usuarios.permisos.access"
        },
        {
          titulo: "Cargos y Responsabilidades",
          routerLink: "/admin/gestion-usuarios/cargos-responsabilidades",
          permiso: "admin.gestion-usuarios.cargos-responsabilidades.access"
        },
      ],
    },
    {
      titulo: "Configuracion Sistema",
      icono: "bi-gear-fill",
      submenu: [
        {
          titulo: "Catalogos de Perfil",
          routerLink: "/admin/configuracion-sistema/catalogos-perfil",
          permiso: "admin.configuracion-sistema.catalogos-perfil.access"
        },
        {
          titulo: "Institucion",
          routerLink: "/admin/configuracion-sistema/institucion",
          permiso: "admin.configuracion-sistema.institucion.access"
        },
        {
          titulo: "Reglas del proyecto de grado",
          routerLink: "/admin/configuracion-sistema/proyecto-grado",
          permiso: "admin.configuracion-sistema.proyecto-grado.access"
        },
        {
          titulo: "Parametros Generales",
          routerLink: "/admin/configuracion-sistema/parametros-generales",
          permiso: "admin.configuracion-sistema.parametros-generales.access"
        },
        {
          titulo: "Periodos Academicos",
          routerLink: "/admin/configuracion-sistema/periodos-academicos",
          permiso: "admin.configuracion-sistema.periodos-academicos.access"
        },
        {
          titulo: "Configuracion Notificaciones",
          routerLink: "/admin/configuracion-sistema/configuracion-notificaciones",
          permiso: "admin.configuracion-sistema.configuracion-notificaciones.access"
        },
        {
          titulo: "Configuracion Temas",
          routerLink: "/admin/configuracion-sistema/configuracion-temas",
          permiso: "admin.configuracion-sistema.configuracion-temas.access"
        },
        {
          titulo: "Logs Sistema",
          routerLink: "/admin/configuracion-sistema/logs-sistema",
          permiso: "admin.configuracion-sistema.logs-sistema.access"
        },
        {
          titulo: "Auditoria Tecnica",
          routerLink: "/admin/configuracion-sistema/auditoria-tecnica",
          permiso: "admin.configuracion-sistema.auditoria-tecnica.access"
        },
      ],
    },
];


export const MENU_DOCENTE: NavItem[] = [
    {
      titulo: "Dashboard",
      icono: "bi-house-fill",
      slug: "dashboard",
      routerLink: "/docente/dashboard",
      permiso: "docente.dashboard.access",
    },
    {
      titulo: "Mis Asignaturas",
      icono: "bi-book-fill",
      submenu: [
        {
          titulo: "Mis Cursos",
          routerLink: "/docente/mis-asignaturas/mis-cursos",
          permiso: "docente.mis-asignaturas.mis-cursos.access"
        },
        {
          titulo: "Contenido Curso",
          routerLink: "/docente/mis-asignaturas/contenido-curso",
          permiso: "docente.mis-asignaturas.contenido-curso.access"
        },
        {
          titulo: "Plan Clase",
          routerLink: "/docente/mis-asignaturas/plan-clase",
          permiso: "docente.mis-asignaturas.plan-clase.access"
        },
        {
          titulo: "Material Apoyo",
          routerLink: "/docente/mis-asignaturas/material-apoyo",
          permiso: "docente.mis-asignaturas.material-apoyo.access"
        },
        {
          titulo: "Proyectos de grado",
          routerLink: "/docente/mis-asignaturas/proyectos-grado",
          permiso: "docente.mis-asignaturas.proyectos-grado.access"
        },
        {
          titulo: "Gestión Contenido IA",
          routerLink: "/docente/mis-asignaturas/contenido-subida",
          permiso: "docente.mis-asignaturas.contenido-subida.access"
        },
      ],
    },
    {
      titulo: "Estudiantes",
      icono: "bi-people-fill",
      submenu: [
        {
          titulo: "Listado Estudiantes",
          routerLink: "/docente/estudiantes/listado-estudiantes",
          permiso: "docente.estudiantes.listado-estudiantes.access"
        },
        {
          titulo: "Seguimiento Academico",
          routerLink: "/docente/estudiantes/seguimiento-academico",
          permiso: "docente.estudiantes.seguimiento-academico.access"
        },
        {
          titulo: "Alertas Academicas",
          routerLink: "/docente/estudiantes/alertas-academicas",
          permiso: "docente.estudiantes.alertas-academicas.access"
        },
        {
          titulo: "Asistencia",
          routerLink: "/docente/estudiantes/asistencia",
          permiso: "docente.estudiantes.asistencia.access"
        },
      ],
    },
    {
      titulo: "Evaluacion",
      icono: "bi-clipboard-check-fill",
      submenu: [
        {
          titulo: "Calificaciones",
          routerLink: "/docente/evaluacion/calificaciones",
          permiso: "docente.evaluacion.calificaciones.access"
        },
        {
          titulo: "Actividades",
          routerLink: "/docente/evaluacion/actividades",
          permiso: "docente.evaluacion.actividades.access"
        },
        {
          titulo: "Evaluaciones",
          routerLink: "/docente/evaluacion/evaluaciones",
          permiso: "docente.evaluacion.evaluaciones.access"
        },
        {
          titulo: "Rubricas",
          routerLink: "/docente/evaluacion/rubricas",
          permiso: "docente.evaluacion.rubricas.access"
        },
      ],
    },
    {
      titulo: "Comunicacion",
      icono: "bi-chat-dots-fill",
      submenu: [
        {
          titulo: "Mensajes Estudiantes",
          routerLink: "/docente/comunicacion/mensajes-estudiantes",
          permiso: "docente.comunicacion.mensajes-estudiantes.access"
        },
        {
          titulo: "Anuncios Curso",
          routerLink: "/docente/comunicacion/anuncios-curso",
          permiso: "docente.comunicacion.anuncios-curso.access"
        },
        {
          titulo: "Foros Academicos",
          routerLink: "/docente/comunicacion/foros-academicos",
          permiso: "docente.comunicacion.foros-academicos.access"
        },
      ],
    },
    {
      titulo: "Investigacion",
      icono: "bi-search",
      submenu: [
        {
          titulo: "Semilleros",
          routerLink: "/docente/investigacion/semilleros",
          permiso: "docente.investigacion.semilleros.access"
        },
        {
          titulo: "Proyectos Investigacion",
          routerLink: "/docente/investigacion/proyectos-investigacion",
          permiso: "docente.investigacion.proyectos-investigacion.access"
        },
        {
          titulo: "Produccion Academica",
          routerLink: "/docente/investigacion/produccion-academica",
          permiso: "docente.investigacion.produccion-academica.access"
        },
      ],
    },
    {
      titulo: "Docentes",
      icono: "bi-person-circle",
      slug: "perfil-docente",
      routerLink: "/docente/perfil-docente",
      permiso: "docente.perfil-docente.access",
    },
    {
      titulo: "Recursos Academicos",
      icono: "bi-journal-bookmark-fill",
      slug: "recursos-academicos",
      routerLink: "/docente/recursos-academicos",
      permiso: "docente.recursos-academicos.access",
    },
];


export const MENU_ESTUDIANTE: NavItem[] = [
    {
      titulo: "Dashboard",
      icono: "bi-house-fill",
      slug: "dashboard",
      routerLink: "/estudiante/dashboard",
      permiso: "estudiante.dashboard.access",
    },
    {
      titulo: "Mi Cronograma",
      icono: "bi-calendar-date-fill",
      submenu: [
        {
          // Es el tablero que antes era el "Dashboard". Comparte el permiso del
          // inicio para que los roles ya sembrados lo vean sin correr el seeder;
          // si luego se quiere conceder por separado, va con código propio.
          titulo: "Mis clases",
          routerLink: "/estudiante/mi-cronograma/mis-clases",
          permiso: "estudiante.dashboard.access"
        },
        {
          titulo: "Contenido Programa",
          routerLink: "/estudiante/mi-cronograma/contenido-programa",
          permiso: "estudiante.mi-cronograma.contenido-programa.access"
        },
        {
          titulo: "Historial Academico",
          routerLink: "/estudiante/mi-cronograma/historial-academico",
          permiso: "estudiante.mi-cronograma.historial-academico.access"
        },
        {
          titulo: "Certificados",
          routerLink: "/estudiante/mi-cronograma/certificados",
          permiso: "estudiante.mi-cronograma.certificados.access"
        },
        {
          titulo: "Orientacion Academica",
          routerLink: "/estudiante/mi-cronograma/orientacion-academica",
          permiso: "estudiante.mi-cronograma.orientacion-academica.access"
        },
      ],
    },
    {
      titulo: "Pagos",
      icono: "bi-credit-card-fill",
      submenu: [
        {
          titulo: "Pago Matricula",
          routerLink: "/estudiante/pagos/pago-matricula",
          permiso: "estudiante.pagos.pago-matricula.access"
        },
        {
          titulo: "Historial Academico",
          routerLink: "/estudiante/pagos/historial-academico",
          permiso: "estudiante.pagos.historial-academico.access"
        },
        {
          titulo: "Certificados",
          routerLink: "/estudiante/pagos/certificados",
          permiso: "estudiante.pagos.certificados.access"
        },
        {
          titulo: "Orientacion Academica",
          routerLink: "/estudiante/pagos/orientacion-academica",
          permiso: "estudiante.pagos.orientacion-academica.access"
        },
      ],
    },
    {
      titulo: "Recursos Academicos",
      icono: "bi-journal-bookmark-fill",
      submenu: [
        {
          titulo: "Biblioteca",
          routerLink: "/estudiante/recursos-academicos/biblioteca",
          permiso: "estudiante.recursos-academicos.biblioteca.access"
        },
        {
          titulo: "Soporte",
          routerLink: "/estudiante/recursos-academicos/soporte",
          permiso: "estudiante.recursos-academicos.soporte.access"
        },
        {
          titulo: "Reproductor Inteligente",
          routerLink: "/test-player",
          permiso: "estudiante.test-player.access"
        },
        {
          titulo: "Mis Videos",
          routerLink: "/test-content",
          permiso: "estudiante.test-content.access"
        },
      ],
    },
    {
      titulo: "Investigaciones",
      icono: "bi-search",
      submenu: [
        {
          titulo: "Quienes Somos",
          routerLink: "/estudiante/investigaciones/quienes-somos",
          permiso: "estudiante.investigaciones.quienes-somos.access"
        },
        {
          titulo: "Coleccion Recursos",
          routerLink: "/estudiante/investigaciones/coleccion-recursos",
          permiso: "estudiante.investigaciones.coleccion-recursos.access"
        },
        {
          titulo: "Solicitar Orientacion",
          routerLink: "/estudiante/investigaciones/solicitar-orientacion",
          permiso: "estudiante.investigaciones.solicitar-orientacion.access"
        },
      ],
    },
    {
      titulo: "Extension Proy Social",
      icono: "bi-globe",
      submenu: [
        {
          titulo: "Quienes Somos",
          routerLink: "/estudiante/extension-proy-social/quienes-somos",
          permiso: "estudiante.extension-proy-social.quienes-somos.access"
        },
        {
          titulo: "Cursos Diplomados",
          routerLink: "/estudiante/extension-proy-social/cursos-diplomados",
          permiso: "estudiante.extension-proy-social.cursos-diplomados.access"
        },
        {
          titulo: "Webinars",
          routerLink: "/estudiante/extension-proy-social/webinars",
          permiso: "estudiante.extension-proy-social.webinars.access"
        },
        {
          titulo: "Mis Inscripcioness",
          routerLink: "/estudiante/extension-proy-social/mis-inscripcioness",
          permiso: "estudiante.extension-proy-social.mis-inscripcioness.access"
        },
      ],
    },
    {
      titulo: "Bienestar Institucional",
      icono: "bi-heart-fill",
      submenu: [
        {
          titulo: "Orientacion Psicosocial",
          routerLink: "/estudiante/bienestar-institucional/orientacion-psicosocial",
          permiso: "estudiante.bienestar-institucional.orientacion-psicosocial.access"
        },
        {
          titulo: "Servicios Bienestar",
          routerLink: "/estudiante/bienestar-institucional/servicios-bienestar",
          permiso: "estudiante.bienestar-institucional.servicios-bienestar.access"
        },
        {
          titulo: "Actividades Formativas",
          routerLink: "/estudiante/bienestar-institucional/actividades-formativas",
          permiso: "estudiante.bienestar-institucional.actividades-formativas.access"
        },
        {
          titulo: "Feria Emprendimiento",
          routerLink: "/estudiante/bienestar-institucional/feria-emprendimiento",
          permiso: "estudiante.bienestar-institucional.feria-emprendimiento.access"
        },
      ],
    },
    {
      titulo: "Matricula Asignaturas",
      icono: "bi-pencil-square",
      slug: "matricula-asignaturas",
      routerLink: "/estudiante/matricula-asignaturas",
      permiso: "estudiante.matricula-asignaturas.access",
    },
    {
      titulo: "Egresados",
      icono: "bi-award-fill",
      slug: "egresados",
      routerLink: "/estudiante/egresados",
      permiso: "estudiante.egresados.access",
    },
    {
      titulo: "Area Social",
      icono: "bi-people-fill",
      slug: "area-social",
      routerLink: "/estudiante/area-social",
      permiso: "estudiante.area-social.access",
    },
];


export const MENUS_POR_AREA: Record<AreaApp, NavItem[]> = {
  admin: MENU_ADMIN,
  docente: MENU_DOCENTE,
  estudiante: MENU_ESTUDIANTE
};

/** Nombre del rol en base de datos para cada área. */
export const ROL_POR_AREA: Record<AreaApp, string> = {
  admin: 'ADMINISTRATIVO',
  docente: 'DOCENTE',
  estudiante: 'ESTUDIANTE'
};

/** Área que corresponde a cada rol; el inverso de ROL_POR_AREA. */
export const AREA_POR_ROL: Record<string, AreaApp> = {
  ADMINISTRATIVO: 'admin',
  ADMIN: 'admin',
  DOCENTE: 'docente',
  ESTUDIANTE: 'estudiante'
};

/** How each role appears next to the brand. */
export const ROLE_BADGE: Record<AreaApp, { label: string; icon: string }> = {
  admin: { label: 'Administrativo', icon: 'bi-person-fill-gear' },
  docente: { label: 'Docente', icon: 'bi-person-badge-fill' },
  estudiante: { label: 'Estudiante', icon: 'bi-person-circle' }
};

export const RUTA_INICIO_POR_AREA: Record<AreaApp, string> = {
  admin: '/admin/dashboard',
  docente: '/docente/dashboard',
  // /estudiante/dashboard redirige a Mis clases; se deja la ruta corta como entrada estable.
  estudiante: '/estudiante/dashboard'
};
